import {
  ChevronBottomOutlineIcon,
  ChevronTopOutlineIcon,
  CutlerySolidIcon,
  MailOutlineIcon,
} from '@ikyucom/design-system-icons-react'
import * as Collapsible from '@radix-ui/react-collapsible'
import { useEffect, useRef, useState } from 'react'
import { LabelNormal } from '~/components/LabelNormal'
import { useIsRefClamped } from '~/hooks/useIsRefClamped'
import { USE_SCENE_LABEL } from '~/lib/constants/constants'
import { isEmpty } from '~/lib/empty'
import type { FragmentType } from '~/lib/graphql'
import { getFragmentData } from '~/lib/graphql'
import { useIsPC } from '~/lib/ua'
import { cva, sva } from '~/styled-system/css'
import { ReviewsNodesFragment } from '../../reviewsNode.frag'

const styles = sva({
  slots: ['root', 'content', 'trigger'],
  base: {
    root: {
      spaceY: '3',
      _pc: {
        fontSize: 'md',
      },
    },
    content: {
      spaceY: '3',
    },
    trigger: {
      _pc: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        w: 'full',
        p: '1',
        columnGap: '1',
        color: 'link',
        fontSize: 'sm',
      },
      _sd: {
        mx: 'auto',
        display: 'block',
      },
    },
  },
})

export function ReviewComments({
  review: maskedReview,
}: { review: FragmentType<typeof ReviewsNodesFragment> }) {
  const review = getFragmentData(ReviewsNodesFragment, maskedReview)
  const { open, setOpen, setHasClampedUserComment, showReadMore } = useReviewComments(maskedReview)
  const classes = styles({ open })

  if (isEmpty(review.userComment) && isEmpty(review.restaurantComment)) {
    return null
  }

  return (
    <Collapsible.Root open={open} onOpenChange={setOpen} className={classes.root}>
      <UserComment open={open} review={maskedReview} onClampDetected={setHasClampedUserComment} />
      <Collapsible.Content className={classes.content}>
        <ReplyComment review={maskedReview} />
        <Plan review={maskedReview} />
      </Collapsible.Content>
      {showReadMore && (
        <Collapsible.Trigger className={classes.trigger}>
          <ReadMore open={open} />
        </Collapsible.Trigger>
      )}
    </Collapsible.Root>
  )
}

function useReviewComments(review: FragmentType<typeof ReviewsNodesFragment>) {
  const [open, setOpen] = useState(false)
  const [hasClampedUserComment, setHasClampedUserComment] = useState(false)
  const showReadMore = showReadMoreLabel(review, hasClampedUserComment)
  return { open, setOpen, setHasClampedUserComment, showReadMore }
}

function showReadMoreLabel(
  review: FragmentType<typeof ReviewsNodesFragment>,
  hasClampedUserComment: boolean
) {
  const {
    __typename: typename,
    restaurantComment,
    userComment,
  } = getFragmentData(ReviewsNodesFragment, review)
  // LYクチコミでない場合は常に表示する
  if (typename !== 'LyReview') {
    return true
  }
  // LYクチコミの場合、ユーザーコメントがクランプされる状態か、または店舗返信コメントがある場合に表示する
  return (!isEmpty(userComment) && hasClampedUserComment) || !isEmpty(restaurantComment)
}
if (import.meta.vitest) {
  const { describe, test, expect } = import.meta.vitest
  type Review = FragmentType<typeof ReviewsNodesFragment>
  describe('showReadMoreLabel', () => {
    test('LYコメントではない', () => {
      const review = {
        __typename: 'Review',
        restaurantComment: null,
        userComment: 'userComment',
      } as Review
      const hasClampedUserComment = false
      expect(showReadMoreLabel(review, hasClampedUserComment)).toBe(true)
    })
    test('レストラン/ユーザーのコメントなし', () => {
      const review = {
        __typename: 'LyReview',
        restaurantComment: null,
        userComment: null,
      } as Review
      const hasClampedUserComment = false
      expect(showReadMoreLabel(review, hasClampedUserComment)).toBe(false)
    })
    test('レストランコメントあり/ユーザーコメントなし', () => {
      const review = {
        __typename: 'LyReview',
        restaurantComment: 'restaurantComment',
        userComment: null,
      } as Review
      const hasClampedUserComment = false
      expect(showReadMoreLabel(review, hasClampedUserComment)).toBe(true)
    })
    test('レストランコメントなし/ユーザーコメントあり/ユーザーコメントのクランプなし', () => {
      const review = {
        __typename: 'LyReview',
        restaurantComment: '',
        userComment: 'userComment',
      } as Review
      const hasClampedUserComment = false
      expect(showReadMoreLabel(review, hasClampedUserComment)).toBe(false)
    })
    test('レストランコメントなし/ユーザーコメントあり/ユーザーコメントのクランプあり', () => {
      const review = {
        __typename: 'LyReview',
        restaurantComment: '',
        userComment: 'userComment',
      } as Review
      const hasClampedUserComment = true
      expect(showReadMoreLabel(review, hasClampedUserComment)).toBe(true)
    })
    test('レストラン/ユーザーコメントあり', () => {
      const review = {
        __typename: 'LyReview',
        restaurantComment: 'restaurantComment',
        userComment: 'userComment',
      } as Review
      const hasClampedUserComment = false
      expect(showReadMoreLabel(review, hasClampedUserComment)).toBe(true)
    })
  })
}

const userCommentStyle = cva({
  variants: {
    open: {
      true: { whiteSpace: 'pre-wrap' },
      false: { lineClamp: { base: '3', _pc: '4' } },
    },
  },
})

function UserComment({
  open,
  review: _review,
  onClampDetected,
}: {
  open: boolean
  review: FragmentType<typeof ReviewsNodesFragment>
  onClampDetected: (clamped: boolean) => void
}) {
  const review = getFragmentData(ReviewsNodesFragment, _review)
  const ref = useRef<HTMLParagraphElement>(null)
  const isClamped = useIsRefClamped(ref)

  useEffect(() => {
    onClampDetected(isClamped)
  }, [isClamped, onClampDetected])

  if (isEmpty(review.userComment)) {
    return null
  }

  return (
    <p ref={ref} itemProp="reviewBody" className={userCommentStyle({ open })}>
      {review.userComment}
    </p>
  )
}

const replyStyles = sva({
  slots: ['container', 'heading', 'icon', 'title', 'comment'],
  base: {
    container: {
      bg: 'gray.100',
      _pc: {
        p: '4',
        spaceY: '3',
        rounded: 'sm',
      },
      _sd: {
        p: '3',
      },
    },
    heading: {
      mb: '2',
      display: 'flex',
      alignItems: 'center',
      _pc: {
        fontSize: 'md',
      },
      _sd: {
        columnGap: '1',
      },
    },
    icon: {
      _pc: {
        mr: '1',
      },
      _sd: {
        color: 'primary',
      },
    },
    title: {
      fontWeight: 'bold',
      _sd: {
        fontSize: 'sm',
        color: 'gray.700',
      },
    },
    comment: {
      whiteSpace: 'pre-wrap',
    },
  },
})

function ReplyComment({ review: _review }: { review: FragmentType<typeof ReviewsNodesFragment> }) {
  const isPc = useIsPC()
  const iconSize = isPc ? 14 : 12
  const review = getFragmentData(ReviewsNodesFragment, _review)
  const classes = replyStyles()

  if (isEmpty(review.restaurantComment)) {
    return null
  }

  return (
    <section className={classes.container}>
      <div className={classes.heading}>
        <MailOutlineIcon width={iconSize} height={iconSize} className={classes.icon} />
        <h3 className={classes.title}>店舗からの返信</h3>
      </div>
      <p className={classes.comment}>{review.restaurantComment}</p>
    </section>
  )
}

const planStyles = sva({
  slots: ['container', 'heading', 'cutleryIcon', 'title', 'sceneLabel'],
  base: {
    container: {
      mt: '2',
      border: 'medium',
      p: '3',
      _pc: {
        fontSize: 'base',
      },
      _sd: {
        fontSize: 'sm',
      },
    },
    heading: {
      mb: '2',
      display: 'flex',
      alignItems: 'center',
      _pc: {
        fontSize: 'base',
      },
      _sd: {
        columnGap: '1',
      },
    },
    cutleryIcon: {
      color: 'primary',
    },
    title: {
      fontWeight: 'bold',
      color: 'gray.700',
    },
    sceneLabel: {
      ml: '3',
    },
  },
})

function Plan({ review: _review }: { review: FragmentType<typeof ReviewsNodesFragment> }) {
  const isPc = useIsPC()
  const review = getFragmentData(ReviewsNodesFragment, _review)
  const classes = planStyles()

  if (review.__typename === 'LyReview') {
    return null
  }

  return (
    <section
      itemProp="itemReviewed"
      itemScope
      itemType="https://schema.org/Product"
      className={classes.container}
    >
      <div className={classes.heading}>
        {!isPc && <CutlerySolidIcon width={12} height={12} className={classes.cutleryIcon} />}
        <h3 className={classes.title}>ご利用内容</h3>
        {review.useScene !== 'OTHER' && (
          <LabelNormal text={USE_SCENE_LABEL[review.useScene]} className={classes.sceneLabel} />
        )}
      </div>
      <p itemProp="name">{review.planName}</p>
    </section>
  )
}

const readMoreStyle = cva({
  variants: {
    isPc: {
      true: { display: 'contents' },
      false: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'link',
        fontSize: 'xs',
        columnGap: '1',
      },
    },
  },
})

function ReadMore({ open }: { open: boolean }) {
  const isPc = useIsPC()
  return (
    <div className={readMoreStyle({ isPc })}>
      <ReadMoreLabel open={open} />
    </div>
  )
}

function ReadMoreLabel({ open }: { open: boolean }) {
  return open ? (
    <>
      <span>閉じる</span>
      <ChevronTopOutlineIcon width={12} height={12} />
    </>
  ) : (
    <>
      <span>もっとみる</span>
      <ChevronBottomOutlineIcon width={12} height={12} />
    </>
  )
}
